export const FINANCIAL_CALENDAR_SCOPE = {
  WEEK: 'week',
  MONTH: 'month',
  YEAR: 'year',
} as const;

export type CalendarScope =
  (typeof FINANCIAL_CALENDAR_SCOPE)[keyof typeof FINANCIAL_CALENDAR_SCOPE];

export const CALENDAR_SCOPE_OPTIONS: { value: CalendarScope; label: string }[] = [
  { value: FINANCIAL_CALENDAR_SCOPE.WEEK, label: 'Semana' },
  { value: FINANCIAL_CALENDAR_SCOPE.MONTH, label: 'Mês' },
  { value: FINANCIAL_CALENDAR_SCOPE.YEAR, label: 'Ano' },
];

export const WEEKDAY_SHORT = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
export const WEEKDAY_LETTER = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'];
export const MONTH_NAMES = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
];

const pad = (n: number) => String(n).padStart(2, '0');

/** 'YYYY-MM-DD' a partir de uma data local. */
export const toDateKey = (date: Date): string =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

/** Data local a partir de 'YYYY-MM-DD' (ou de um ISO cujo prefixo seja a data). */
export const fromDateKey = (key: string): Date => {
  const [y, m, d] = key.slice(0, 10).split('-').map(Number);
  return new Date(y, m - 1, d);
};

/** Extrai a chave de dia de qualquer string de data vinda da API. */
export const dateKeyFromApi = (value: string | null | undefined): string | null => {
  if (!value) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value.trim());
  return match ? `${match[1]}-${match[2]}-${match[3]}` : null;
};

export const startOfDay = (date: Date): Date =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate());

export const addDays = (date: Date, days: number): Date => {
  const next = startOfDay(date);
  next.setDate(next.getDate() + days);
  return next;
};

/** Semana começa no domingo (padrão pt-BR / iOS). */
export const startOfWeek = (date: Date): Date => addDays(date, -date.getDay());
export const endOfWeek = (date: Date): Date => addDays(startOfWeek(date), 6);

export const startOfMonth = (date: Date): Date =>
  new Date(date.getFullYear(), date.getMonth(), 1);
export const endOfMonth = (date: Date): Date =>
  new Date(date.getFullYear(), date.getMonth() + 1, 0);

export const isSameDay = (a: Date, b: Date): boolean =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

export interface DateRange {
  from: Date;
  to: Date;
}

/** Período "selecionado" (base dos totais). */
export const periodBounds = (scope: CalendarScope, anchor: Date): DateRange => {
  switch (scope) {
    case FINANCIAL_CALENDAR_SCOPE.WEEK:
      return { from: startOfWeek(anchor), to: endOfWeek(anchor) };
    case FINANCIAL_CALENDAR_SCOPE.YEAR:
      return {
        from: new Date(anchor.getFullYear(), 0, 1),
        to: new Date(anchor.getFullYear(), 11, 31),
      };
    default:
      return { from: startOfMonth(anchor), to: endOfMonth(anchor) };
  }
};

/** Intervalo visível na grade (no mês inclui os dias dos meses vizinhos). */
export const gridBounds = (scope: CalendarScope, anchor: Date): DateRange => {
  if (scope === FINANCIAL_CALENDAR_SCOPE.MONTH) {
    return {
      from: startOfWeek(startOfMonth(anchor)),
      to: endOfWeek(endOfMonth(anchor)),
    };
  }
  return periodBounds(scope, anchor);
};

export const shiftAnchor = (scope: CalendarScope, anchor: Date, delta: number): Date => {
  switch (scope) {
    case FINANCIAL_CALENDAR_SCOPE.WEEK:
      return addDays(anchor, delta * 7);
    case FINANCIAL_CALENDAR_SCOPE.YEAR:
      return new Date(anchor.getFullYear() + delta, anchor.getMonth(), 1);
    default:
      return new Date(anchor.getFullYear(), anchor.getMonth() + delta, 1);
  }
};

const capitalize = (value: string) => value.charAt(0).toUpperCase() + value.slice(1);

export const periodLabel = (scope: CalendarScope, anchor: Date): string => {
  switch (scope) {
    case FINANCIAL_CALENDAR_SCOPE.WEEK: {
      const { from, to } = periodBounds(scope, anchor);
      const sameMonth = from.getMonth() === to.getMonth();
      const fromLabel = sameMonth
        ? String(from.getDate())
        : from.toLocaleDateString('pt-BR', { day: 'numeric', month: 'short' });
      const toLabel = to.toLocaleDateString('pt-BR', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      });
      return `${fromLabel} – ${toLabel}`;
    }
    case FINANCIAL_CALENDAR_SCOPE.YEAR:
      return String(anchor.getFullYear());
    default:
      return capitalize(
        anchor.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' }),
      );
  }
};

/** Semanas (linhas de 7 dias) que cobrem o mês do `anchor`. */
export const monthWeeks = (anchor: Date): Date[][] => {
  const { from, to } = gridBounds(FINANCIAL_CALENDAR_SCOPE.MONTH, anchor);
  const weeks: Date[][] = [];
  let cursor = from;
  while (cursor <= to) {
    const week: Date[] = [];
    for (let i = 0; i < 7; i += 1) {
      week.push(cursor);
      cursor = addDays(cursor, 1);
    }
    weeks.push(week);
  }
  return weeks;
};

export const weekDays = (anchor: Date): Date[] => {
  const start = startOfWeek(anchor);
  return Array.from({ length: 7 }, (_, i) => addDays(start, i));
};
